import { useState, useEffect, useRef } from 'react'

/**
 * Hook that provides asymmetric timing for collapse/expand UI transitions.
 *
 * - Collapsing: Returns true immediately when isCollapsed becomes true
 * - Expanding: Returns false only after the animation duration completes
 *
 * This creates a better UX where the collapsed UI appears instantly on collapse,
 * but the expanded UI waits for the animation to finish before appearing.
 */
export function useDelayedExpand(
  isCollapsed: boolean,
  animationDuration = 150,
): boolean {
  const [showCollapsed, setShowCollapsed] = useState(isCollapsed)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (isCollapsed) {
      setShowCollapsed(true)
    } else {
      timeoutRef.current = setTimeout(() => {
        setShowCollapsed(false)
      }, animationDuration)
    }

    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isCollapsed, animationDuration])

  return showCollapsed
}
